// src/modules/admin/AspirantometroDashboard.js
import React, { useState, useEffect } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { formatarData } from '../../utils/helpers';
import { AspirantometroCard } from '../../components/AspirantometroCard'; // Importa o Card

// Ícones simples
const CalendarIcon = () => (
    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);
const EditIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
);
const DeleteIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
);

export const AspirantometroDashboard = () => {
    const { userProfile } = useAuth();
    const [aspirantometros, setAspirantometros] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados do Modal CRUD
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ titulo: '', data_final: '' });
    const [currentItem, setCurrentItem] = useState(null); // Para edição
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    
    // Estado do Modal de Confirmação
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);


    // Carrega os Aspirantômetros em tempo real
    useEffect(() => {
        setLoading(true);
        const unsub = onSnapshot(collection(db, paths.aspirantometros), (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAspirantometros(list.sort((a, b) => new Date(a.data_final) - new Date(b.data_final)));
            setLoading(false);
        }, (err) => {
            console.error("Erro ao carregar aspirantômetros:", err);
            setLoading(false);
        });

        return () => unsub();
    }, []);

    // --- Handlers CRUD ---
    const handleOpenModal = (item = null) => {
        setCurrentItem(item);
        setFormData(item ? { titulo: item.titulo, data_final: item.data_final } : { titulo: '', data_final: '' });
        setError('');
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setError('');
        
        const { titulo, data_final } = formData;
        if (!titulo || !data_final) {
            setError("Título e Data Final são obrigatórios.");
            setProcessing(false);
            return;
        }

        try {
            if (currentItem) {
                // Edição
                const docRef = doc(db, paths.aspirantometros, currentItem.id);
                await setDoc(docRef, formData);
                await logAuditoria(userProfile, "Atualização Aspirantômetro", formData);
            } else {
                // Criação
                const newDocRef = doc(collection(db, paths.aspirantometros));
                const dataToSave = { ...formData, id: newDocRef.id };
                await setDoc(newDocRef, dataToSave);
                await logAuditoria(userProfile, "Criação Aspirantômetro", dataToSave);
            }
            setShowModal(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setProcessing(false);
        }
    };
    
    const handleConfirmDelete = (item) => {
        setItemToDelete(item);
        setError('');
        setShowConfirm(true);
    };
    
    const handleDelete = async () => {
        if (!itemToDelete) return;
        setProcessing(true);
        
        try {
            await deleteDoc(doc(db, paths.aspirantometros, itemToDelete.id));
            await logAuditoria(userProfile, "Exclusão Aspirantômetro", itemToDelete);
            setShowConfirm(false);
            setItemToDelete(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <LoadingSpinner text="Carregando Aspirantômetros..." />;

    return (
        <div>
            <header className="mb-6 pb-4 border-b">
                <h2 className="text-3xl font-bold text-gray-800">
                    <CalendarIcon /> Gestão do Aspirantômetro
                </h2>
                <p className="text-gray-600">Crie, edite e organize os cronômetros de contagem regressiva para os cadetes.</p>
            </header>

            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Aspirantômetros Cadastrados ({aspirantometros.length})</h3>
                <Button onClick={() => handleOpenModal()} variant="success">
                    + Novo Aspirantômetro
                </Button>
            </div>

            {/* Pré-visualização (Cards) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {aspirantometros.map(a => (
                    <AspirantometroCard key={a.id} aspirantometro={a} />
                ))}
                {aspirantometros.length === 0 && (
                    <p className="col-span-3 text-center text-gray-500 py-4">
                        Nenhum Aspirantômetro cadastrado.
                    </p>
                )}
            </div>
            
            {/* Tabela de Gestão */}
            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">Gerenciar Detalhes</h3>
            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data Final</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {aspirantometros.map(a => (
                            <tr key={a.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{a.titulo}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatarData(new Date(a.data_final + 'T12:00:00Z'))}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                    <Button onClick={() => handleOpenModal(a)} variant="outline" className="text-blue-600 hover:text-blue-800">
                                        <EditIcon />
                                    </Button>
                                    <Button onClick={() => handleConfirmDelete(a)} variant="danger" className="text-red-600 hover:text-red-800">
                                        <DeleteIcon />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Modal CRUD */}
            <Modal show={showModal} onClose={() => setShowModal(false)} title={currentItem ? 'Editar Aspirantômetro' : 'Novo Aspirantômetro'} size="sm">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Título (Ex: Aspirantes 2026)</label>
                        <input name="titulo" value={formData.titulo || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Data Final</label>
                        <input name="data_final" type="date" value={formData.data_final || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
                    </div>
                    
                    {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
                    
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button onClick={() => setShowModal(false)} variant="secondary" disabled={processing}>Cancelar</Button>
                        <Button type="submit" variant="primary" disabled={processing}>
                            {processing ? "Salvando..." : "Salvar Aspirantômetro"}
                        </Button>
                    </div>
                </form>
            </Modal>
            
            {/* Modal de Confirmação de Exclusão */}
            <Modal show={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Exclusão" size="sm">
                <p className="text-gray-700">Tem certeza que deseja excluir o Aspirantômetro **{itemToDelete?.titulo}**?</p>
                <p className="font-semibold text-red-600 mt-2">Esta ação não pode ser desfeita.</p>
                {error && <p className="text-red-600 mt-2">{error}</p>}
                <div className="flex justify-end gap-3 mt-4">
                    <Button onClick={() => setShowConfirm(false)} variant="secondary" disabled={processing}>Cancelar</Button>
                    <Button onClick={handleDelete} variant="danger" disabled={processing}>
                        {processing ? "Excluindo..." : "Excluir"}
                    </Button>
                </div>
            </Modal>
        </div>
    );
};